import { getFileNameFromUrl, uploadFileToBucket } from '@/utils';
import { Flex, Form, type FormInstance } from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useUploadFileMutation } from '../app/features/auth/auth.api';
import { TrashIcon } from '@/icons';
import useAuth from '../hooks/useAuth';
import { toast } from '../lib/toast';

type Props = {
  form: FormInstance<any>;
  name: string;
  title?: string;
  value?: string;
  accept?: string;
  icon?: ReactNode;
  maxSize?: number; // MB
  disabled?: boolean;
  placeholder?: string;
  description?: string;
  errorMessage?: string;
  onChange?: (url: string | null) => void;
};

export default function SingleFileUploader({
  name,
  form,
  value,
  title,
  onChange,
  disabled,
  description,
  maxSize = 5,
  accept = '.png,.jpeg,.svg,.jpg',
  errorMessage = 'File upload failed',
}: Props) {
  const [fileName, setFileName] = useState<string>('');
  const { access_token } = useAuth();
  const [uploadFile, { isLoading }] = useUploadFileMutation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isUpdating, setIsUploading] = useState(false);
  const loading = isLoading || isUpdating;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !access_token) return;

    const maxBytes = maxSize * 1024 * 1024;
    if (file.size > maxBytes) {
      toast('error', `File "${file.name}" exceeds ${maxSize} MB`);
      return;
    }

    setIsUploading(true);

    try {
      let fileUrl: string;

      if (access_token) {
        const res = await uploadFileToBucket(file, access_token);
        if (!res.success) {
          throw new Error(res.error);
        }
        fileUrl = res.url as string;
      } else {
        const formData = new FormData();
        formData.append('file', file);
        const res = await uploadFile(formData).unwrap();
        fileUrl = res?.Location;
      }

      setFileName(getFileNameFromUrl(fileUrl));
      form?.setFieldValue(name, fileUrl);
      onChange?.(fileUrl);
    } catch {
      toast('error', `${errorMessage}: ${file.name}`);
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = ''; // reset input
    }
  };

  const removeFile = () => {
    setFileName('');
    form?.setFieldValue(name, null);
    onChange?.(null);
  };

  const watchedFile = Form.useWatch(name, form);

  useEffect(() => {
    if (watchedFile && typeof watchedFile === 'string') {
      setFileName(getFileNameFromUrl(watchedFile));
    }
  }, [watchedFile]);

  useEffect(() => {
    if (value && typeof value === 'string') {
      setFileName(getFileNameFromUrl(value));
    }
  }, [value]);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="!hidden"
        disabled={disabled || loading}
        onChange={handleFileChange}
      />

      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => inputRef.current?.click()}
        className="w-full p-6 min-h-[135px] rounded-lg bg-theme-gray-99 dark:bg-theme-slate-900 hover:bg-theme-gray-98 dark:hover:bg-theme-slate-800 active:bg-theme-gray-99 dark:active:bg-theme-slate-900 transition-all border-2 border-dashed border-theme-gray-800 cursor-pointer"
      >
        <div>
          <h4 className="text-theme-gray-50 dark:text-white text-base font-semibold text-center">
            {title || 'Upload document'}
          </h4>
          {description && (
            <p className="text-theme-gray-60 dark:text-white text-base font-normal text-center">
              {description}
            </p>
          )}
        </div>
      </button>

      {fileName && (
        <div className="space-y-2.5 mt-4">
          <Flex
            gap={16}
            align="center"
            justify="space-between"
            className="bg-theme-gray-96 dark:bg-theme-slate-900 px-2 py-1 rounded-md h-8 border border-theme-slate-800"
          >
            <Flex gap={6} align="center">
              <span className="text-sm text-theme-slate-800 dark:text-white font-medium">
                {fileName}
              </span>
            </Flex>
            <button
              type="button"
              onClick={removeFile}
              className="text-theme-error-500"
            >
              <TrashIcon size={16} />
            </button>
          </Flex>
        </div>
      )}
    </>
  );
}
